
import { IntervalConfig, ReportMetadata, SubstitutionConfig } from '../types';
import { formatDateLocal, heuteBerlin, listDueDatesInRange, parseDateLocal, resolveIntervalConfig } from './dateService';
import type { ListProject } from './listProject';
import { buchungstag, isAccountingEntry, isMainInspectionBooking } from './serviceLogUtils';

/**
 * ICS-Erzeugung für Begehungstermine.
 *
 * Drei Ausgaben:
 *  - `generateProjectIcs`    — EIN Projekt als Serie (RRULE, unbegrenzt).
 *  - `generateBerlinIcs`     — beliebige Termine/Serien in Ortszeit Berlin
 *    (Projekt-Kickoff, `services/kickoff/projectKickoff.ts`).
 *  - `generateSubstituteIcs` — alle Projekte EINER Urlaubsvertretung als
 *    einzelne Termine innerhalb des Vertretungszeitraums. Bewusst ohne RRULE:
 *    eine Serie ohne Ende würde im Kalender des Vertreters für immer
 *    weiterlaufen, und `twice_monthly`/`preferredWeekdays` sind als
 *    Wiederholungsregel nicht verlustfrei abbildbar.
 *
 * Serien stehen in Ortszeit mit `TZID=Europe/Berlin` und eigener VTIMEZONE,
 * nicht in UTC: Eine UTC-Serie ohne Ende behält ihre UTC-Uhrzeit und
 * verrutscht nach jeder Zeitumstellung um eine Stunde (08:00 wird 07:00 oder
 * 09:00). Die Vertretung bleibt bei UTC — Einzeltermine rechnet der Kalender
 * je Termin korrekt um, und ihre Ausgabe soll sich nicht ändern.
 */

const UID_DOMAIN = 'sigeko-app';

/** Zeitzone aller Serien — die der Baustellen, nie die des Geräts. */
export const ICS_TZID = 'Europe/Berlin';

/** RFC 5545: Backslash, Semikolon, Komma und Zeilenumbruch maskieren. */
export const escapeIcsText = (value: string | undefined | null): string =>
    String(value ?? '')
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\r?\n/g, '\\n');

/** RFC 5545: Zeilen auf 75 Zeichen falten, Folgezeile beginnt mit Leerzeichen. */
export const foldLine = (line: string): string => {
    if (line.length <= 75) return line;
    const parts: string[] = [line.slice(0, 75)];
    let rest = line.slice(75);
    while (rest.length > 74) {
        parts.push(' ' + rest.slice(0, 74));
        rest = rest.slice(74);
    }
    if (rest.length > 0) parts.push(' ' + rest);
    return parts.join('\r\n');
};

const formatIcsStamp = (date: Date): string =>
    date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

const formatDateReadable = (d?: string): string => {
    const parsed = parseDateLocal(d || '');
    // Feste zweistellige Schreibweise: die Standardausgabe von toLocaleDateString
    // kürzt je nach Laufzeitumgebung auf "5.10.2026" und macht Kalendertexte
    // uneinheitlich.
    return parsed ? parsed.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
};

/** Termin-Datum + "HH:MM" zu einem lokalen Date; Standard 08:00, Dauer 1 h. */
const toStartDate = (dateStr: string, time?: string): Date | null => {
    const base = parseDateLocal(dateStr);
    if (!base) return null;
    const [hours, minutes] = (time || '08:00').split(':').map(Number);
    base.setHours(Number.isFinite(hours) ? hours : 8, Number.isFinite(minutes) ? minutes : 0, 0, 0);
    return base;
};

/**
 * Gemeinsamer Beschreibungstext: Stammdaten, Bauherr, Auftraggeber, Beteiligte.
 * Hub-Download, Vertretung und Kickoff nutzen dieselben Zeilen, damit der
 * Termin im Kalender überall gleich aussieht.
 */
export const buildDescriptionLines = (meta: ReportMetadata): string[] => {
    const gd = meta.globalData;
    const lines: string[] = [
        `Projekt: ${meta.projectName}`,
        `Projekt-Nr: ${meta.projectNumber || '-'}`,
        `Standort: ${meta.projectAddress || '-'}`,
        `Koordinator: ${meta.coordinator || '-'}`,
    ];

    if (gd?.startDate || gd?.endDate) {
        lines.push(`Laufzeit: ${formatDateReadable(gd?.startDate)} - ${formatDateReadable(gd?.endDate)}`);
    }

    lines.push('', '--- BAUHERR ---');
    lines.push(meta.clientName || '-');
    if (meta.clientAddress) lines.push(meta.clientAddress);
    if (meta.clientContactPerson) lines.push(`Kontakt: ${meta.clientContactPerson}`);
    if (meta.clientPhone) lines.push(`Tel: ${meta.clientPhone}`);
    if (meta.clientEmail) lines.push(`Mail: ${meta.clientEmail}`);

    if (meta.customerName && meta.customerName !== meta.clientName) {
        lines.push('', '--- AUFTRAGGEBER ---');
        lines.push(meta.customerName);
        if (meta.customerAddress) lines.push(meta.customerAddress);
        if (meta.customerContactPerson) lines.push(`Kontakt: ${meta.customerContactPerson}`);
        if (meta.customerPhone) lines.push(`Tel: ${meta.customerPhone}`);
        if (meta.customerEmail) lines.push(`Mail: ${meta.customerEmail}`);
    }

    if (meta.distributionList && meta.distributionList.length > 0) {
        lines.push('', '--- BETEILIGTE ---');
        meta.distributionList.forEach(c => {
            const role = c.role ? `(${c.role})` : '';
            const trade = c.tradeType ? `[${c.tradeType}]` : '';
            lines.push(`${c.company}: ${c.name} ${role} ${trade}`.trim());
            if (c.email) lines.push(`  -> ${c.email}`);
        });
    }

    lines.push('', 'Generiert von SiGeKo Smart Report');
    return lines;
};

interface VeventInput {
    uid: string;
    start: Date;
    end: Date;
    summary: string;
    description: string[];
    location?: string;
    rrule?: string;
}

const buildVevent = (input: VeventInput): string[] => {
    const lines = [
        'BEGIN:VEVENT',
        `UID:${input.uid}`,
        `DTSTAMP:${formatIcsStamp(new Date())}`,
        `DTSTART:${formatIcsStamp(input.start)}`,
        `DTEND:${formatIcsStamp(input.end)}`,
        `SUMMARY:${escapeIcsText(input.summary)}`,
        `DESCRIPTION:${escapeIcsText(input.description.join('\n'))}`,
        `LOCATION:${escapeIcsText(input.location || '')}`,
        'STATUS:CONFIRMED',
    ];
    if (input.rrule) lines.push(`RRULE:${input.rrule}`);
    lines.push('END:VEVENT');
    return lines;
};

/**
 * VTIMEZONE für Europe/Berlin: MESZ ab letztem Sonntag im März 02:00, MEZ ab
 * letztem Sonntag im Oktober 03:00. Steht in der Datei selbst, damit Outlook
 * die Ortszeit auch ohne eigene Zonendatenbank richtig liest.
 */
const BERLIN_VTIMEZONE: string[] = [
    'BEGIN:VTIMEZONE',
    `TZID:${ICS_TZID}`,
    'BEGIN:DAYLIGHT',
    'TZOFFSETFROM:+0100',
    'TZOFFSETTO:+0200',
    'TZNAME:CEST',
    'DTSTART:19700329T020000',
    'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU',
    'END:DAYLIGHT',
    'BEGIN:STANDARD',
    'TZOFFSETFROM:+0200',
    'TZOFFSETTO:+0100',
    'TZNAME:CET',
    'DTSTART:19701025T030000',
    'RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU',
    'END:STANDARD',
    'END:VTIMEZONE',
];

const wrapCalendar = (eventLines: string[], zoneLines: string[] = []): string => {
    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//SiGeKo Smart Report//DE',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        ...zoneLines,
        ...eventLines,
        'END:VCALENDAR',
    ];
    return lines.filter(l => l).map(foldLine).join('\r\n');
};

/** "8:00", "08:00:00" → "08:00"; Unlesbares → Standard 08:00 (wie überall in der App). */
export const normalizeTime = (value?: string | null): string => {
    const match = String(value ?? '').trim().match(/^(\d{1,2}):(\d{2})/);
    if (!match) return '08:00';
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) return '08:00';
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/** Ortszeit als ICS-Wert ohne Zonenkennung: `YYYYMMDDTHHMMSS`. */
const formatIcsLocal = (datum: string, uhrzeit: string): string =>
    `${datum.replace(/-/g, '')}T${uhrzeit.replace(':', '')}00`;

/**
 * Ende eines Termins als Wanduhrzeit. Gerechnet über UTC-Felder, damit weder
 * die Zone des Geräts noch eine Zeitumstellung die Dauer verfälscht.
 */
const addMinutesLocal = (datum: string, uhrzeit: string, minuten: number): string => {
    const [y, m, d] = datum.split('-').map(Number);
    const [hh, mm] = uhrzeit.split(':').map(Number);
    const ende = new Date(Date.UTC(y, m - 1, d, hh, mm + minuten, 0));
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${ende.getUTCFullYear()}${pad(ende.getUTCMonth() + 1)}${pad(ende.getUTCDate())}T${pad(ende.getUTCHours())}${pad(ende.getUTCMinutes())}00`;
};

/** ICS-Kürzel je JS-Wochentag (0 = So … 6 = Sa). */
const ICS_WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

/**
 * Gültige `preferredWeekdays`, ohne Doppel, Montag zuerst sortiert
 * (Di + Do → [2, 4], So steht am Ende).
 */
export const sortedPreferredWeekdays = (config: IntervalConfig | null | undefined): number[] => {
    const valid = (config?.preferredWeekdays || []).filter(d => Number.isInteger(d) && d >= 0 && d <= 6);
    return Array.from(new Set(valid)).sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));
};

/**
 * Turnus als ICS-Wiederholungsregel, nie mit UNTIL/COUNT (Serien ohne Ende,
 * Entscheid 11.10.2026); leer = keine abbildbare Serie (`manual`).
 *
 * Wöchentlich und 14-tägig tragen die Wochentage aus `preferredWeekdays` als
 * BYDAY — sonst landet ein Di+Do-Turnus als einfache Wochenserie am Tag des
 * ersten Termins.
 */
export const intervalToRrule = (config: IntervalConfig | null | undefined): string => {
    const byDay = sortedPreferredWeekdays(config).map(d => ICS_WEEKDAYS[d]).join(',');
    const withByDay = (rule: string) => (byDay ? `${rule};BYDAY=${byDay}` : rule);
    switch (config?.type) {
        case 'daily': return 'FREQ=DAILY';
        case 'weekly': return withByDay('FREQ=WEEKLY');
        // WKST=MO ausdrücklich: Die Phase der 14-tägigen Serie rechnen wir in
        // Montagswochen (`seriesStartFromAnchor`); ein Kalender mit Sonntag als
        // Wochenbeginn legte einen Sonntagstermin sonst in die falsche Woche.
        case 'biweekly': return byDay ? `FREQ=WEEKLY;INTERVAL=2;BYDAY=${byDay};WKST=MO` : 'FREQ=WEEKLY;INTERVAL=2';
        case 'monthly': return 'FREQ=MONTHLY';
        // Zweimal im Monat: Näherung auf den 1. und 15. — die App rechnet
        // tagesgenau weiter, die Kalenderserie ist nur die Erinnerung. In der
        // Datei stehen daraus zwei Serien (`splitMonthDaySeries`).
        case 'twice_monthly': return 'FREQ=MONTHLY;BYMONTHDAY=1,15';
        case 'custom': {
            const days = config.customDays;
            return typeof days === 'number' && Number.isInteger(days) && days >= 1
                ? `FREQ=DAILY;INTERVAL=${days}`
                : 'FREQ=WEEKLY';
        }
        default: return '';
    }
};

/**
 * Erster Serientermin am oder nach `datum`, der zur Regel aus
 * `intervalToRrule` passt. RFC 5545 zählt DTSTART immer als ersten Termin;
 * liegt er neben dem Muster (Mittwoch bei BYDAY=TU,TH), zeigt ein Kalender
 * einen Zusatztermin, ein anderer verschluckt ihn.
 *
 *  - `twice_monthly`: nächster 1. oder 15.
 *  - `weekly`/`biweekly` mit Wochentagen (Regel mit BYDAY): erster
 *    passender Wochentag.
 *  - sonst unverändert: Regeln ohne BYDAY (täglich, monatlich, alle n Tage)
 *    wiederholen den Starttag selbst; ihn auf einen Wochentag zu schieben,
 *    änderte nur die Phase der Serie, nicht ihre Wochentage.
 */
export const alignSeriesStart = (datum: string, config: IntervalConfig | null | undefined): string => {
    const base = parseDateLocal(datum);
    if (!base) return datum;
    const type = config?.type;

    if (type === 'twice_monthly') {
        const day = base.getDate();
        if (day === 1 || day === 15) return formatDateLocal(base);
        const next = new Date(base);
        if (day < 15) next.setDate(15);
        else next.setMonth(next.getMonth() + 1, 1);
        return formatDateLocal(next);
    }

    const weekdays = sortedPreferredWeekdays(config);
    if ((type !== 'weekly' && type !== 'biweekly') || weekdays.length === 0) return formatDateLocal(base);
    const cursor = new Date(base);
    for (let i = 0; i < 7; i++) {
        if (weekdays.includes(cursor.getDay())) return formatDateLocal(cursor);
        cursor.setDate(cursor.getDate() + 1);
    }
    return formatDateLocal(base);
};

// ── Serienanker ─────────────────────────────────────────────────────────────

const TAG_MS = 24 * 60 * 60 * 1000;

/**
 * Ganze Tage zwischen zwei Daten aus `parseDateLocal` (beide 12:00 Ortszeit).
 * Gerundet, weil ein Tag mit Zeitumstellung 23 oder 25 Stunden hat.
 */
const tageZwischen = (von: Date, bis: Date): number => Math.round((bis.getTime() - von.getTime()) / TAG_MS);

const plusTage = (date: Date, tage: number): Date => {
    const next = new Date(date);
    next.setDate(next.getDate() + tage);
    return next;
};

/** Montag der Woche (ISO-Woche, WKST=MO wie in `intervalToRrule`). */
const montagDerWoche = (date: Date): Date => plusTage(date, -((date.getDay() + 6) % 7));

/** Ab dem Anker in ganzen Perioden zu `step` Tagen vorrücken, bis ≥ heute. */
const vorrueckenUmTage = (anker: Date, heute: Date, step: number): string => {
    const diff = tageZwischen(anker, heute);
    if (diff <= 0) return formatDateLocal(anker);
    return formatDateLocal(plusTage(anker, Math.ceil(diff / step) * step));
};

/**
 * Erster Termin am Monatstag `tag` ab dem Monat von `start`, der ≥ `abKey`
 * liegt. Monate ohne diesen Tag (31., 30., 29.) überspringt auch RFC 5545 —
 * die Serie und ihr erster Termin passen so zueinander.
 */
const monatstagAb = (start: Date, tag: number, abKey: string): string => {
    for (let k = 0; k < 2400; k++) {
        const jahr = start.getFullYear();
        const monat = start.getMonth() + k;
        if (new Date(jahr, monat + 1, 0).getDate() < tag) continue;
        const kandidat = formatDateLocal(new Date(jahr, monat, tag, 12, 0, 0, 0));
        if (kandidat >= abKey) return kandidat;
    }
    return abKey;
};

/** Monatlich: derselbe Monatstag wie der Anker, erster ≥ heute. */
const vorrueckenUmMonate = (anker: Date, heuteKey: string): string => monatstagAb(anker, anker.getDate(), heuteKey);

/**
 * Erster passender Wochentag ab `abKey`, dessen Woche in der Phase der Serie
 * liegt (14-tägig: jede zweite Montagswoche).
 *
 * Die Phase beginnt mit der Woche des ERSTEN passenden Wochentags ab dem
 * Anker, nicht mit der Ankerwoche selbst. Sonst fiele bei Baustart Do 19.11.
 * und Turnus „alle 2 Wochen montags" der Montag 16.11. (vor dem Baustart) in
 * die Phase, und die erste Begehung rutschte vom 23.11. auf den 30.11.
 */
const ersterWochentagInPhase = (anker: Date, abKey: string, weekdays: number[], wochen: number): string => {
    let ersterTreffer = anker;
    for (let i = 0; i < 7; i++) {
        const tag = plusTage(anker, i);
        if (weekdays.includes(tag.getDay())) { ersterTreffer = tag; break; }
    }
    const ankerMontag = montagDerWoche(ersterTreffer);
    const cursor = parseDateLocal(abKey)!;
    for (let i = 0; i < 7 * wochen + 7; i++) {
        const wochenAbstand = Math.round(tageZwischen(ankerMontag, montagDerWoche(cursor)) / 7);
        if (weekdays.includes(cursor.getDay()) && wochenAbstand % wochen === 0) return formatDateLocal(cursor);
        cursor.setDate(cursor.getDate() + 1);
    }
    return abKey;
};

/**
 * Erster Termin einer Serie ab `heute`, abgeleitet aus einem FESTEN Anker.
 *
 * Wochentag und Phase der Serie hängen nur am Anker, nie am Abrufdatum. Würde
 * der Start bei jedem Abruf an „heute" ausgerichtet, wechselte eine Wochen-
 * serie ohne feste Wochentage zwischen Montagsmail und Dienstagspopup den
 * Wochentag, und 14-tägige Serien verschöben ihre Phase. Mit derselben UID
 * überschriebe jeder Import dann die Serie des vorigen.
 *
 *  - Anker in der Zukunft: der Anker selbst (auf die Regel ausgerichtet).
 *  - Anker in der Vergangenheit: in ganzen Perioden der Regel vorrücken
 *    (täglich 1, wöchentlich 7, 14-tägig 14, alle n Tage n; monatlich über
 *    den Monatstag), bis ≥ heute.
 *  - Mit Wochentagen (BYDAY): Woche und Phase vom Anker, Tag = erster
 *    passender Wochentag ≥ max(Anker, heute).
 *  - `twice_monthly`: nächster 1. oder 15. ab max(Anker, heute).
 *  - Ohne Serie (`manual`): der Anker unverändert.
 */
export const seriesStartFromAnchor = (anker: string, heute: string, config: IntervalConfig | null | undefined): string => {
    const a = parseDateLocal(anker);
    const h = parseDateLocal(heute);
    if (!a) return h ? alignSeriesStart(formatDateLocal(h), config) : anker;
    const ankerKey = formatDateLocal(a);
    if (!h) return alignSeriesStart(ankerKey, config);
    const heuteKey = formatDateLocal(h);
    const ab = ankerKey > heuteKey ? ankerKey : heuteKey;

    switch (config?.type) {
        case 'daily': return vorrueckenUmTage(a, h, 1);
        case 'weekly':
        case 'biweekly': {
            const wochen = config.type === 'biweekly' ? 2 : 1;
            const weekdays = sortedPreferredWeekdays(config);
            return weekdays.length > 0
                ? ersterWochentagInPhase(a, ab, weekdays, wochen)
                : vorrueckenUmTage(a, h, 7 * wochen);
        }
        case 'monthly': return vorrueckenUmMonate(a, heuteKey);
        case 'twice_monthly': return alignSeriesStart(ab, config);
        case 'custom': {
            const n = config.customDays;
            // Ohne gültige Tageszahl schreibt intervalToRrule eine Wochenserie.
            return vorrueckenUmTage(a, h, typeof n === 'number' && Number.isInteger(n) && n >= 1 ? n : 7);
        }
        default: return ankerKey;
    }
};

/**
 * Wöchentlich oder 14-tägig OHNE feste Wochentage: der Tag der Begehung
 * steht nicht fest, ihn legt die Tourenplanung fest (Betreiber 14.10.2026:
 * „der Wochentag ist zu 99 % nicht fix“). Im Kalender steht dann ein
 * ganztägiger Merker am Montag der fälligen Woche, als „frei“ markiert —
 * eine Serie um 08:00 am Wochentag des Ankers wäre ein erfundener Termin,
 * der echte Zeit blockiert.
 *
 * `datum` ist der erste fällige Termin aus `seriesStartFromAnchor`; der
 * Merker beginnt am Montag derselben Woche. Die Phase der 14-tägigen Serie
 * bleibt so erhalten: verschoben wird nur der Tag, nie die Woche.
 * `null` = kein Merker, die Serie bleibt mit Uhrzeit.
 */
export const wochenMerker = (
    config: IntervalConfig | null | undefined,
    datum: string,
): { datum: string; rrule: string } | null => {
    if (config?.type !== 'weekly' && config?.type !== 'biweekly') return null;
    if (sortedPreferredWeekdays(config).length > 0) return null;
    const d = parseDateLocal(datum);
    if (!d) return null;
    return {
        datum: formatDateLocal(montagDerWoche(d)),
        rrule: config.type === 'biweekly' ? 'FREQ=WEEKLY;INTERVAL=2;BYDAY=MO;WKST=MO' : 'FREQ=WEEKLY;BYDAY=MO',
    };
};

/** Betreff der Begehungsserie — Kickoff und Hub-Download tragen dieselbe UID und müssen gleich heißen. */
export const begehungTitel = (projNum: string, projName: string, merker: boolean): string => {
    const kopf = [projNum, merker ? 'Begehung diese Woche' : 'Begehung'].filter(Boolean).join(' ');
    return projName ? `${kopf} – ${projName}` : kopf;
};

/** Felder einer `projects`-Zeile, die der Serienanker liest — locker typisiert für Teilabfragen. */
interface AnkerZeile {
    next_scheduled_date?: unknown;
    start_date?: unknown;
    data?: { metadata?: unknown } | null;
}

/**
 * Ein Projekt, wie Hub-Download und Urlaubsvertretung es lesen: Listeneintrag
 * (`ListProject`) oder volle Projektzeile. Gelesen werden nur Spalten, die der
 * Listenauszug auch traegt; `metadata` steht wie in `AnkerZeile` als `unknown`,
 * weil Aufrufer und Fixtures Teil-Metadaten hereinreichen — gelesen wird sie
 * ueber `readMeta` bzw. `ankerMeta`.
 */
export type CalendarProject = Pick<ListProject, 'id'>
    & Partial<Pick<ListProject, 'name' | 'project_number' | 'next_scheduled_date' | 'start_date'>>
    & { data?: { metadata?: unknown } | null };

const alsAnkerZeile = (project: unknown): AnkerZeile =>
    project && typeof project === 'object' ? (project as AnkerZeile) : {};

const ankerMeta = (project: unknown): Partial<ReportMetadata> =>
    (alsAnkerZeile(project).data?.metadata as Partial<ReportMetadata> | undefined) || {};

const alsDatum = (value: unknown): string | null => {
    const d = parseDateLocal(typeof value === 'string' ? value : null);
    return d ? formatDateLocal(d) : null;
};

/** Baustart: `globalData.startDate`, sonst die Spalte `start_date`. */
export const projectStartDate = (project: unknown): string | null =>
    alsDatum(ankerMeta(project).globalData?.startDate) ?? alsDatum(alsAnkerZeile(project).start_date);

/**
 * Anker der Begehungsserie, Rangfolge wie `getEffectiveDueDate` und die
 * Ankerregel der Vertretungsmail (docs/vertretung-kalendermail.md): Spalte
 * `next_scheduled_date`, sonst `intervalConfig.nextScheduledDate` — der
 * GEPLANTE nächste Termin, den „Verschieben" im Dashboard ändert —, sonst der
 * Baustart. `null`: nichts davon gepflegt.
 */
export const inspectionSeriesAnchor = (project: unknown): string | null =>
    alsDatum(alsAnkerZeile(project).next_scheduled_date)
    ?? alsDatum(ankerMeta(project).intervalConfig?.nextScheduledDate)
    ?? projectStartDate(project);

/** Ein Termin oder eine Serie in Ortszeit Berlin. */
export interface BerlinEventInput {
    uid: string;
    /** Erster Termin, `YYYY-MM-DD`. */
    datum: string;
    /** `HH:MM`, Ortszeit Berlin. */
    uhrzeit: string;
    dauerMin: number;
    summary: string;
    description: string[];
    location?: string;
    /** RRULE ohne DTSTART; fehlt = Einzeltermin. */
    rrule?: string;
    /** Folgemails mit derselben UID erhöhen sie (Abschluss, Terminänderung). */
    sequence?: number;
    /**
     * Ganztägiger Merker (`VALUE=DATE`), als frei markiert — blockiert in
     * Outlook keine Zeit. `uhrzeit` und `dauerMin` gelten dann nicht.
     */
    ganztags?: boolean;
}

const buildBerlinVevent = (input: BerlinEventInput): string[] => {
    const uhrzeit = normalizeTime(input.uhrzeit);
    const zeitraum = input.ganztags
        ? [
            `DTSTART;VALUE=DATE:${input.datum.replace(/-/g, '')}`,
            `DTEND;VALUE=DATE:${addMinutesLocal(input.datum, '00:00', 24 * 60).slice(0, 8)}`,
        ]
        : [
            `DTSTART;TZID=${ICS_TZID}:${formatIcsLocal(input.datum, uhrzeit)}`,
            `DTEND;TZID=${ICS_TZID}:${addMinutesLocal(input.datum, uhrzeit, input.dauerMin)}`,
        ];
    const lines = [
        'BEGIN:VEVENT',
        `UID:${input.uid}`,
        `SEQUENCE:${input.sequence ?? 0}`,
        `DTSTAMP:${formatIcsStamp(new Date())}`,
        ...zeitraum,
        `SUMMARY:${escapeIcsText(input.summary)}`,
        `DESCRIPTION:${escapeIcsText(input.description.join('\n'))}`,
        `LOCATION:${escapeIcsText(input.location || '')}`,
        'STATUS:CONFIRMED',
    ];
    if (input.ganztags) lines.push('TRANSP:TRANSPARENT', 'X-MICROSOFT-CDO-BUSYSTATUS:FREE');
    if (input.rrule) lines.push(`RRULE:${input.rrule}`);
    lines.push('END:VEVENT');
    return lines;
};

/** `abc@sigeko-app` + `-tag15` → `abc-tag15@sigeko-app`. */
const uidMitSuffix = (uid: string, suffix: string): string => {
    const at = uid.lastIndexOf('@');
    return at < 0 ? `${uid}${suffix}` : `${uid.slice(0, at)}${suffix}${uid.slice(at)}`;
};

/**
 * Monatsserie mit mehreren Monatstagen (`twice_monthly`: BYMONTHDAY=1,15) als
 * je eine Serie pro Tag. Exchange kennt je Monatsserie nur EINEN Tag; Outlook
 * Web lehnt sonst die ganze Datei ab („Fehler bei der Inhaltskonvertierung",
 * Projekt 26101016, 22.10.2026).
 *
 * Der kleinste Tag behält die UID — Hub-Download und Kickoff treffen so
 * weiter dieselbe Begehungsserie —, jeder weitere bekommt `-tag<N>` vor dem
 * `@`. Jede Teilserie beginnt am ersten passenden Monatstag ab `datum`.
 */
const splitMonthDaySeries = (event: BerlinEventInput): BerlinEventInput[] => {
    const teile = (event.rrule || '').split(';');
    const byMonthDay = teile.find(t => t.startsWith('BYMONTHDAY='));
    const base = parseDateLocal(event.datum);
    if (!teile.includes('FREQ=MONTHLY') || !byMonthDay || !base) return [event];
    const tage = byMonthDay.slice('BYMONTHDAY='.length).split(',').map(Number);
    if (tage.length < 2 || !tage.every(t => Number.isInteger(t) && t >= 1 && t <= 31)) return [event];
    const abKey = formatDateLocal(base);
    return Array.from(new Set(tage)).sort((a, b) => a - b).map((tag, i) => ({
        ...event,
        uid: i === 0 ? event.uid : uidMitSuffix(event.uid, `-tag${tag}`),
        datum: monatstagAb(base, tag, abKey),
        rrule: teile.map(t => (t === byMonthDay ? `BYMONTHDAY=${tag}` : t)).join(';'),
    }));
};

/**
 * EINE Kalenderdatei mit Terminen und Serien in Ortszeit Berlin (VTIMEZONE +
 * `DTSTART;TZID=Europe/Berlin`). Termine mit kaputtem Datum fallen weg,
 * statt die ganze Datei unlesbar zu machen.
 */
export const generateBerlinIcs = (events: BerlinEventInput[]): string => {
    const eventLines: string[] = [];
    events.forEach(event => {
        if (!parseDateLocal(event.datum)) return;
        splitMonthDaySeries(event).forEach(teil => eventLines.push(...buildBerlinVevent(teil)));
    });
    return wrapCalendar(eventLines, BERLIN_VTIMEZONE);
};

/**
 * ICS-Datei für die Begehungsserie EINES Projekts (Hub-Download).
 *
 * Turnus aus `resolveIntervalConfig` wie Urlaubsplaner und Kickoff — SiFa
 * hat keinen Begehungsturnus und bekommt deshalb keine Serie, auch mit
 * Altwert im Blob. Dieselbe UID und derselbe Serienanker wie die
 * Begehungsserie des Kickoffs (`inspectionSeriesAnchor` +
 * `seriesStartFromAnchor`), damit ein erneuter Import die Serie aktualisiert
 * statt sie zu verdoppeln oder ihren Wochentag zu verschieben. Ohne jeden
 * Anker gilt das Berichtsdatum, sonst heute.
 *
 * `heute` nur für Tests; Standard ist der heutige Tag in Berlin.
 */
export const generateProjectIcs = (project: CalendarProject, heute: string = heuteBerlin()): string => {
    const meta = project.data.metadata as ReportMetadata;
    const interval = resolveIntervalConfig(meta);
    const rrule = intervalToRrule(interval);

    const anker = inspectionSeriesAnchor(project) ?? alsDatum(meta.date) ?? alsDatum(heute) ?? heuteBerlin();
    const start = rrule ? seriesStartFromAnchor(anker, heute, interval) : anker;
    // Ohne feste Wochentage: Montags-Merker statt Uhrzeit, wie im Kickoff.
    const merker = rrule ? wochenMerker(interval, start) : null;

    const projNum = project.project_number || meta.projectNumber || '';
    const projName = meta.projectName || project.name || '';

    return generateBerlinIcs([{
        uid: `${project.id}@${UID_DOMAIN}`,
        datum: merker?.datum ?? start,
        uhrzeit: normalizeTime(meta.time),
        dauerMin: 60,
        // Gleicher Betreff wie die Begehungsserie aus der Kickoff-Mail
        // (projectKickoff.ts): beide tragen dieselbe UID, ein Import aus dem
        // Hub würde den Titel der Serie sonst jedes Mal zurückbenennen.
        summary: begehungTitel(projNum, projName, !!merker),
        description: buildDescriptionLines(meta),
        location: meta.projectAddress,
        rrule: merker?.rrule ?? (rrule || undefined),
        ...(merker ? { ganztags: true } : {}),
    }]);
};

export const sanitizeFileName = (name: string): string =>
    (name || 'Datei').replace(/[^a-z0-9_\-]/gi, '_');

export const downloadIcsContent = (icsContent: string, filename: string) => {
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const downloadIcs = (project: CalendarProject) => {
    // ankerMeta = `project.data?.metadata || {}`, nur typisiert.
    const meta = ankerMeta(project);
    const projNum = project.project_number || meta.projectNumber || '';
    const projName = project.name || meta.projectName || 'Projekt';
    const rawFileName = projNum ? `${projNum}_${projName}` : projName;
    downloadIcsContent(generateProjectIcs(project), `Termine_${sanitizeFileName(rawFileName)}.ics`);
};

// ── Urlaubsvertretung ───────────────────────────────────────────────────────

/** Ein Projekt, das ein Vertreter in einem Zeitraum übernimmt. */
export interface SubstituteAssignment {
    project: CalendarProject;
    entry: SubstitutionConfig;
}

/** Ein konkreter Begehungstermin innerhalb einer Vertretung. */
export interface SubstituteAppointment {
    projectId: string;
    projectNumber: string;
    projectName: string;
    projectAddress: string;
    coordinator: string;
    date: string;
    startDate: string;
    endDate: string;
}

const readMeta = (project: CalendarProject | null | undefined): ReportMetadata | null =>
    (project?.data?.metadata as ReportMetadata) || null;

/**
 * Tag der letzten versendeten Begehung laut Protokoll: die Hauptbuchung
 * „Bericht #N abgeschlossen" (auch als Nachweiszeile auf der Pauschale), ohne
 * Buchungsvorgaenge. Sie traegt das Berichtsdatum, aus dem die App den
 * geplanten naechsten Termin rechnet — genau das braucht der Wochenplan in
 * `listDueDatesInRange` (`lastDoneDate`). Ohne Treffer `null`, dann bleibt
 * alles wie bisher.
 */
export const lastReportBookingDay = (meta: ReportMetadata | null | undefined): string | null => {
    const log = meta?.globalData?.serviceLog;
    if (!Array.isArray(log)) return null;
    let latest: string | null = null;
    for (const e of log) {
        if (!e || !isMainInspectionBooking(e) || isAccountingEntry(e)) continue;
        const day = buchungstag(e.date);
        if (day && (!latest || day > latest)) latest = day;
    }
    return latest;
};

/**
 * Begehungstermine eines Projekts im Vertretungszeitraum. Turnus und
 * geplanter nächster Termin kommen aus derselben Auflösung wie im
 * Urlaubsplaner — Planung, Mail und ICS dürfen sich nicht widersprechen.
 *
 * `lastDone`: Tag der letzten Begehung, wenn der Aufrufer ihn genauer kennt
 * (Vertretungsliste); sonst aus dem Protokoll (`lastReportBookingDay`).
 */
export const substituteDueDates = (project: CalendarProject, entry: SubstitutionConfig, max = 60, lastDone?: string | null): string[] => {
    const meta = readMeta(project);
    if (!meta) return [];
    return listDueDatesInRange(
        resolveIntervalConfig(meta),
        meta.date,
        entry.startDate,
        entry.endDate,
        max,
        project.next_scheduled_date,
        lastDone ?? lastReportBookingDay(meta),
    );
};

/**
 * Konkrete Begehungstermine je Vertretungseintrag, chronologisch.
 *
 * Projekte ohne berechenbaren Turnus (`manual`) liefern keinen Termin und
 * fehlen hier — sie stehen trotzdem in der Projektliste der Mail, damit der
 * Vertreter sie nicht übersieht.
 */
export const listSubstituteAppointments = (assignments: SubstituteAssignment[]): SubstituteAppointment[] => {
    const out: SubstituteAppointment[] = [];
    assignments.forEach(({ project, entry }) => {
        const meta = readMeta(project);
        if (!meta) return;
        substituteDueDates(project, entry).forEach(date => {
            out.push({
                projectId: project.id,
                projectNumber: project.project_number || meta.projectNumber || '',
                projectName: meta.projectName || project.name || 'Begehung',
                projectAddress: meta.projectAddress || '',
                coordinator: meta.coordinator || '',
                date,
                startDate: entry.startDate,
                endDate: entry.endDate,
            });
        });
    });
    return out.sort((a, b) => a.date.localeCompare(b.date) || a.projectNumber.localeCompare(b.projectNumber));
};

/**
 * Eine ICS-Datei mit allen Vertretungsterminen eines Vertreters.
 *
 * Jeder Termin bekommt eine eigene UID mit Vertretungs-Suffix. Ohne das
 * Suffix würde Outlook den Termin als Änderung der Stammserie des
 * Koordinators verstehen und dessen Eintrag überschreiben.
 */
export const generateSubstituteIcs = (assignments: SubstituteAssignment[]): string => {
    const eventLines: string[] = [];

    assignments.forEach(({ project, entry }) => {
        const meta = readMeta(project);
        if (!meta) return;
        const dates = substituteDueDates(project, entry);
        if (dates.length === 0) return;

        const projNum = project.project_number || meta.projectNumber || '';
        const prefix = projNum ? `${projNum}_` : '';
        const baseDescription = buildDescriptionLines(meta);

        dates.forEach(date => {
            const start = toStartDate(date, meta.time);
            if (!start) return;
            const end = new Date(start);
            end.setHours(start.getHours() + 1);

            eventLines.push(...buildVevent({
                uid: `${project.id}-sub-${entry.startDate}-${date}@${UID_DOMAIN}`,
                start,
                end,
                summary: `[V] ${prefix}${meta.projectName || project.name || 'Begehung'}`,
                description: [
                    `Urlaubsvertretung für: ${meta.coordinator || 'Stamm-Koordinator'}`,
                    `Vertretungszeitraum: ${formatDateReadable(entry.startDate)} - ${formatDateReadable(entry.endDate)}`,
                    '',
                    ...baseDescription,
                ],
                location: meta.projectAddress,
            }));
        });
    });

    return wrapCalendar(eventLines);
};
